import { CAPACITY_FACTORS, get_capacity_factor } from "../../data/capacity_factor_data"
import { AggregatedBuiltPowerPlantsByH3R4Id } from "../../data/power_plants/interface"
import { AllCapacityFactorData } from "../../data/wind_and_solar_capacity/load_data"
import { SimStateByH3R4Id } from "./interface"


/**
 * Compute hourly generation (MW) and stored (MWh) for each aggregated H3 cell.
 * datetime indices should be the hourly integer indices used by the capacity factor data.
 * ~~A mix between two adjacent datetime indices can be provided to interpolate.~~
 */
export function get_sim_state_by_h3r4_id(
    demand_GW: number,
    proportional_electricity_demand_GW_by_h3r4: Record<string, number>,
    capacity_factor_data: AllCapacityFactorData,
    aggregated_built_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id,
    previous_sim_state_by_h3r4_id: SimStateByH3R4Id | undefined,
    datetime_index: number,
): SimStateByH3R4Id
{
    const wind_cf = capacity_factor_data.wind
    const solar_cf = capacity_factor_data.solar

    const sim_state_by_h3r4_id: SimStateByH3R4Id = {}
    let total_generated_MW = 0

    const h3r4_ids = new Set<string>([
        ...Object.keys(aggregated_built_power_plants_by_h3r4_id),
        ...Object.keys(proportional_electricity_demand_GW_by_h3r4),
    ])

    for (const h3r4_id of h3r4_ids)
    {
        const data = aggregated_built_power_plants_by_h3r4_id[h3r4_id]

        const wind_capacity_MW = data?.wind.capacity_MW ?? 0
        const solar_capacity_MW = data?.solar.capacity_MW ?? 0
        const hydro_river_capacity_MW = data?.hydro_river.capacity_MW ?? 0
        const nuclear_capacity_MW = data?.nuclear.capacity_MW ?? 0

        // const cf_wind = get_capacity_factor_mix(wind_cf, datetime_index, datetime_index2, mix, h3_id) ?? 0
        const capacity_factor_wind = get_capacity_factor(wind_cf, datetime_index, h3r4_id) ?? 0
        const capacity_factor_solar = get_capacity_factor(solar_cf, datetime_index, h3r4_id) ?? 0

        const wind_generated_MW = wind_capacity_MW * capacity_factor_wind
        const solar_generated_MW = solar_capacity_MW * capacity_factor_solar
        const nuclear_generated_MW = nuclear_capacity_MW * CAPACITY_FACTORS.nuclear
        const hydro_river_generated_MW = hydro_river_capacity_MW * CAPACITY_FACTORS.hydro_river

        const total_generated_MW_for_cell = (
            wind_generated_MW
            + solar_generated_MW
            + nuclear_generated_MW
            + hydro_river_generated_MW
        )

        const previous = previous_sim_state_by_h3r4_id?.[h3r4_id]

        sim_state_by_h3r4_id[h3r4_id] = {
            demand_GW: demand_GW * (proportional_electricity_demand_GW_by_h3r4[h3r4_id] ?? 0),
            power_plants: {
                wind: { generated_MW: wind_generated_MW },
                solar: { generated_MW: solar_generated_MW },
                gas: { generated_MW: 0 },
                nuclear: { generated_MW: nuclear_generated_MW },
                hydro_river: { generated_MW: hydro_river_generated_MW },
                battery: {
                    generated_MW: 0,
                    stored_MWh: previous?.power_plants?.battery.stored_MWh ?? ((data?.battery.store_capacity_MWh ?? 0) * 0.5),
                },
                hydro_pumped_storage: {
                    generated_MW: 0,
                    stored_MWh: previous?.power_plants?.hydro_pumped_storage.stored_MWh ?? ((data?.hydro_pumped_storage.store_capacity_MWh ?? 0) * 0.5),
                },
                total_generated_MW: {
                    primary: total_generated_MW_for_cell,
                    storage: 0,
                    total_positive: total_generated_MW_for_cell,
                },
            }
        }

        total_generated_MW += total_generated_MW_for_cell
    }

    // If diff_MW is positive (excess generation) try to store it in batteries
    // or pumped storage.  If diff_MW is negative (shortage) try to discharge
    // batteries or pumped storage, or produce from gas.
    let diff_MW = total_generated_MW - (demand_GW * 1000)

    if (diff_MW > 0)
    {
        diff_MW = mutate__store_excess_generation_in_hydro_pumped_storage(diff_MW, aggregated_built_power_plants_by_h3r4_id, sim_state_by_h3r4_id)
        diff_MW = mutate__store_excess_generation_in_battery(diff_MW, aggregated_built_power_plants_by_h3r4_id, sim_state_by_h3r4_id)
    }
    else
    {
        diff_MW = mutate__discharge_hydro_pumped_storage_to_meet_demand(diff_MW, aggregated_built_power_plants_by_h3r4_id, sim_state_by_h3r4_id)
        diff_MW = mutate__discharge_battery_to_meet_demand(diff_MW, aggregated_built_power_plants_by_h3r4_id, sim_state_by_h3r4_id)
        if (false) diff_MW = mutate__run_ccgt_to_meet_demand(diff_MW, aggregated_built_power_plants_by_h3r4_id, sim_state_by_h3r4_id)
    }

    return sim_state_by_h3r4_id
}


function mutate__store_excess_generation_in_battery(diff_MW: number, aggregated_built_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id, sim_state_by_h3r4_id: SimStateByH3R4Id): number
{
    for (const [h3r4_id, data] of Object.entries(sim_state_by_h3r4_id))
    {
        if (diff_MW <= 0) return diff_MW
        const power_plants = data.power_plants
        if (!power_plants) continue
        const battery_status = power_plants.battery
        if (!battery_status) continue
        const built_battery = aggregated_built_power_plants_by_h3r4_id[h3r4_id]?.battery
        if (!built_battery) continue

        const battery_stored_capacity_MWh = Math.min(
            diff_MW,
            built_battery.store_capacity_MWh - (battery_status.stored_MWh ?? 0),
            built_battery.capacity_MW,
        )
        diff_MW -= battery_stored_capacity_MWh
        battery_status.stored_MWh += battery_stored_capacity_MWh
        battery_status.generated_MW = -battery_stored_capacity_MWh
        power_plants.total_generated_MW.storage -= battery_stored_capacity_MWh
    }

    return diff_MW
}
function mutate__store_excess_generation_in_hydro_pumped_storage(diff_MW: number, aggregated_built_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id, sim_state_by_h3r4_id: SimStateByH3R4Id): number
{
    for (const [h3r4_id, data] of Object.entries(sim_state_by_h3r4_id))
    {
        if (diff_MW <= 0) return diff_MW
        const power_plants = data.power_plants
        if (!power_plants) continue
        const hydro_pumped_storage_status = power_plants.hydro_pumped_storage
        if (!hydro_pumped_storage_status) continue
        const built_hydro_pumped_storage = aggregated_built_power_plants_by_h3r4_id[h3r4_id]?.hydro_pumped_storage
        if (!built_hydro_pumped_storage) continue

        const hydro_pumped_storage_stored_capacity_MWh = Math.min(
            diff_MW,
            built_hydro_pumped_storage.store_capacity_MWh - (hydro_pumped_storage_status.stored_MWh ?? 0),
            built_hydro_pumped_storage.capacity_MW,
        )
        diff_MW -= hydro_pumped_storage_stored_capacity_MWh
        hydro_pumped_storage_status.stored_MWh += hydro_pumped_storage_stored_capacity_MWh
        hydro_pumped_storage_status.generated_MW = -hydro_pumped_storage_stored_capacity_MWh
        power_plants.total_generated_MW.storage -= hydro_pumped_storage_stored_capacity_MWh
    }

    return diff_MW
}

function mutate__discharge_battery_to_meet_demand(diff_MW: number, aggregated_built_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id, sim_state_by_h3r4_id: SimStateByH3R4Id): number
{
    for (const [h3r4_id, data] of Object.entries(sim_state_by_h3r4_id))
    {
        if (diff_MW >= 0) return diff_MW
        const power_plants = data.power_plants
        if (!power_plants) continue
        const battery_status = power_plants.battery
        if (!battery_status) continue
        const built_battery = aggregated_built_power_plants_by_h3r4_id[h3r4_id]?.battery
        if (!built_battery) continue

        const battery_discharge_MW = Math.min(
            -diff_MW,
            battery_status.stored_MWh,
            built_battery.capacity_MW
        )
        diff_MW += battery_discharge_MW
        battery_status.stored_MWh -= battery_discharge_MW
        battery_status.generated_MW = battery_discharge_MW
        power_plants.total_generated_MW.storage += battery_discharge_MW
        power_plants.total_generated_MW.total_positive += battery_discharge_MW
    }

    return diff_MW
}
function mutate__discharge_hydro_pumped_storage_to_meet_demand(diff_MW: number, aggregated_built_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id, sim_state_by_h3r4_id: SimStateByH3R4Id): number
{
    for (const [h3r4_id, data] of Object.entries(sim_state_by_h3r4_id))
    {
        if (diff_MW >= 0) return diff_MW
        const power_plants = data.power_plants
        if (!power_plants) continue
        const hydro_pumped_storage_status = power_plants.hydro_pumped_storage
        if (!hydro_pumped_storage_status) continue
        const built_hydro_pumped_storage = aggregated_built_power_plants_by_h3r4_id[h3r4_id]?.hydro_pumped_storage
        if (!built_hydro_pumped_storage) continue

        const hydro_pumped_storage_discharge_MW = Math.min(
            -diff_MW,
            hydro_pumped_storage_status.stored_MWh,
            built_hydro_pumped_storage.capacity_MW
        )
        diff_MW += hydro_pumped_storage_discharge_MW
        hydro_pumped_storage_status.stored_MWh -= hydro_pumped_storage_discharge_MW
        hydro_pumped_storage_status.generated_MW = hydro_pumped_storage_discharge_MW
        power_plants.total_generated_MW.storage += hydro_pumped_storage_discharge_MW
        power_plants.total_generated_MW.total_positive += hydro_pumped_storage_discharge_MW
    }

    return diff_MW
}
function mutate__run_ccgt_to_meet_demand(diff_MW: number, aggregated_built_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id, sim_state_by_h3r4_id: SimStateByH3R4Id): number
{
    for (const [h3r4_id, data] of Object.entries(sim_state_by_h3r4_id))
    {
        if (diff_MW >= 0) return diff_MW
        const power_plants = data.power_plants
        if (!power_plants) continue
        const gas_status = power_plants.gas
        if (!gas_status) continue
        const built_gas = aggregated_built_power_plants_by_h3r4_id[h3r4_id]?.gas
        if (!built_gas) continue

        const gas_discharge_MW = Math.min(
            -diff_MW,
            built_gas.capacity_MW
        )
        diff_MW += gas_discharge_MW
        gas_status.generated_MW = gas_discharge_MW
        power_plants.total_generated_MW.primary += gas_discharge_MW
        power_plants.total_generated_MW.total_positive += gas_discharge_MW
    }

    return diff_MW
}
